import React, { useState, useEffect } from "react";
import "./Signup.css";
import { auth, db } from "../Security/firebase-config";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { getDoc, setDoc, doc, serverTimestamp } from "firebase/firestore";
import { createUserRecord } from "../utils/createUserRecord";
import { useLocation, useNavigate } from "react-router-dom";
import CustomAlert from "../components/core-components/CustomAlert";




const Signup = () => {
  const [alert, setAlert] = useState({ message: "", type: "" });
  const showAlert = (message, type = "info") => {
    setAlert({ message, type });
    setTimeout(() => setAlert({ message: "", type: "" }), 5000);
  };

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    emailError: "",
    passwordError: "",
    usernameError: "",
  });

  const [visibleSections, setVisibleSections] = useState({
    emailFields: false,
    passwordFields: false,
    finalElements: false,
  });

  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    specialChar: false,
    uppercase: false,
    number: false,
  });

  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  const { search } = useLocation();
  const navigate = useNavigate();
  const next = new URLSearchParams(search).get("next") || "/account";


  // ✅ Mobile-safe progressive reveal:
  // Autofill often doesn't trigger your onFocus chain, so reveal based on input content.
  useEffect(() => {
    const usernameOk = formData.username.trim().length > 0;

    const emailOk =
      formData.email.trim().length > 0 &&
      formData.confirmEmail.trim().length > 0;

    const pwOk =
      formData.password.length > 0 &&
      formData.confirmPassword.length > 0;

    setVisibleSections((prev) => ({
      emailFields: prev.emailFields || usernameOk,
      passwordFields: prev.passwordFields || emailOk,
      finalElements: prev.finalElements || pwOk,
    }));
  }, [
    formData.username,
    formData.email,
    formData.confirmEmail,
    formData.password,
    formData.confirmPassword,
  ]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });

    if (id === "password") {
      const lengthCheck = value.length >= 6;
      const specialCharCheck = /[@$!%*?&]/.test(value);
      const uppercaseCheck = /[A-Z]/.test(value);
      const numberCheck = /\d/.test(value);

      setPasswordChecks({
        length: lengthCheck,
        specialChar: specialCharCheck,
        uppercase: uppercaseCheck,
        number: numberCheck,
      });

      const allValid = lengthCheck && specialCharCheck && uppercaseCheck && numberCheck;
      setShowPasswordRequirements(!allValid);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, email, confirmEmail, password, confirmPassword } = formData;
    const usernameTrimmed = username.trim();
    const uname = usernameTrimmed.toLowerCase();
    if (!usernameTrimmed) {
      setErrors((prev) => ({ ...prev, usernameError: "Username cannot be empty." }));
      showAlert("Please enter a username.", "error");
      return;
    }

    let valid = true;
  
    setErrors({ emailError: "", passwordError: "", usernameError: "" });
  
    // Check email match
    if (email !== confirmEmail) {
      setErrors((prev) => ({ ...prev, emailError: "Emails do not match." }));
      showAlert("Email addresses do not match. Please try again.", "error");
      valid = false;
    }
  
    // Check password match
    if (password !== confirmPassword) {
      setErrors((prev) => ({ ...prev, passwordError: "Passwords do not match." }));
      showAlert("Passwords do not match. Please re-enter them.", "error");
      valid = false;
    }
  
    // Check password strength
    const passwordMeetsRequirements =
      passwordChecks.length &&
      passwordChecks.specialChar &&
      passwordChecks.uppercase &&
      passwordChecks.number;
  
    if (!passwordMeetsRequirements) {
      showAlert("Password does not meet the requirements.", "error");
      valid = false;
    }
  
    // Check if username is taken (via /usernames index)
    try {
      const usernameRef = doc(db, "usernames", uname);
      const snap = await getDoc(usernameRef);

      if (snap.exists()) {
        setErrors((prev) => ({ ...prev, usernameError: "Username is already taken." }));
        showAlert("That username is already taken. Try another one.", "error");
        valid = false;
      }
    } catch (error) {
      showAlert("Could not validate username. Please try again.", "error");
      console.error("Username validation failed:", error);
      return;
    }


  
    if (!valid) return;
  
    try {
      // Firebase signup
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      await sendEmailVerification(userCredential.user);

      // Standardized Firestore user doc
      await createUserRecord(userCredential.user, {
        username: usernameTrimmed,
        needsEmailVerification: true,
        emailVerified: false,
        verifiedUnlockedAt: null,
      });

      // Create username index doc (immutable)

      try {
        await setDoc(doc(db, "usernames", uname), {
          uid: userCredential.user.uid,
          username: usernameTrimmed,
          createdAt: serverTimestamp(),
        });

        await setDoc(doc(db, "uids", userCredential.user.uid), {
          uid: userCredential.user.uid,
          username: usernameTrimmed,
          unameLower: uname,
          createdAt: serverTimestamp(),
        });
      } catch (e) {
        console.error("Username index write failed:", e);
        showAlert(
          "Account created, but username indexing failed. Please try a different username or contact support.",
          "error"
        );
        // You can still navigate to verify email, but username-login may not work until fixed.
      }

      showAlert("Account created! Please verify your email to unlock full access.", "success");

      // go to verify page immediately
      navigate(`/verify-email?next=${encodeURIComponent(next)}`, { replace: true });
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        // <-- clearer message if they previously used Google/Discord
        showAlert(
          "That email is already in use. If you signed up with Google or Discord, log in with that provider first, then add a password in Account → 'Add Email & Password Login'.",
          "error"
        );
      } else {
        showAlert("Signup failed: " + error.message, "error");
      }
    }
  };

  const toggleVisibility = (section) => {
    setVisibleSections((prev) => ({ ...prev, [section]: true }));
  };

  const togglePasswordVisibility = (id) => {
    const input = document.getElementById(id);
    input.type = input.type === "password" ? "text" : "password";
  };

  

  return (
    
    <div className="signup-page-wrapper">
      {alert.message && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ message: "", type: "" })}
        />
      )}
    
      <div id="signup-container">
        
        <form id="signup-form" onSubmit={handleSubmit}>
          <h2>Sign Up</h2>
          {/* Username Field */}
          <input
            type="text"
            id="username"
            name="username"
            autoComplete="username"
            className="input-field visible"
            placeholder="Username"
            required
            value={formData.username}
            onChange={handleChange}
            onFocus={() => toggleVisibility("emailFields")}
          />

          {errors.usernameError && <span id="username-error" className="error-message">{errors.usernameError}</span>}

          {/* Email Fields */}
          {visibleSections.emailFields && (
            <div id="email-fields" className="visible">
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                className="input-field"
                placeholder="Email"
                required
                value={formData.email}
                onChange={handleChange}
              />

              <input
                type="email"
                id="confirmEmail"
                name="confirmEmail"
                autoComplete="off"
                className="input-field"
                placeholder="Confirm Email"
                required
                value={formData.confirmEmail}
                onChange={handleChange}
                onFocus={() => toggleVisibility("passwordFields")}
              />

              {errors.emailError && <span id="email-error" className="error-message">{errors.emailError}</span>}
            </div>
          )}

          {/* Password Fields */}
          {visibleSections.passwordFields && (
            <div id="password-fields" className="visible">
              <div className="password-container">
                <input
                  type="password"
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  className="input-field"
                  placeholder="Password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => { toggleVisibility("finalElements"); setShowPasswordRequirements(true); }}
                  onBlur={() => setShowPasswordRequirements(false)}
                />

                <i
                  className="fa-solid fa-eye password-icon"
                  onClick={() => togglePasswordVisibility("password")}
                ></i>
              </div>

              {/* Password Requirements Box */}
              {showPasswordRequirements && (
                <div className="password-requirements-box">
                  <p className="Pw-strong">Password must contain at least:</p>
                  <ul>
                    <li>
                      <span className={passwordChecks.length ? "pw-dot ok" : "pw-dot bad"} aria-hidden="true" />
                      6 characters
                    </li>

                    <li>
                      <span className={passwordChecks.specialChar ? "pw-dot ok" : "pw-dot bad"} aria-hidden="true" />
                      1 special character (e.g., @$!%*?&)
                    </li>
                    <li>
                      <span className={passwordChecks.uppercase ? "pw-dot ok" : "pw-dot bad"} aria-hidden="true" />
                      1 uppercase letter
                    </li>
                    <li>
                      <span className={passwordChecks.number ? "pw-dot ok" : "pw-dot bad"} aria-hidden="true" />
                      1 number
                    </li>
                  </ul>
              </div>
              
              )}

              <div className="password-container">
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  className="input-field"
                  placeholder="Confirm Password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <i
                  className="fa-solid fa-eye password-icon"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                ></i>
              </div>
              {errors.passwordError && (
                <span id="password-error" className="error-message">{errors.passwordError}</span>
              )}
            </div>
          )}



          {/* Submit Button and Login Link */}
          {visibleSections.finalElements && (
            <div id="final-elements" className="visible">
              <button type="submit" id="signup-btn">Sign Up</button>
              <p>
                Already have an account?{" "}
                <a href="/login" className="login-link">Login</a>
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Signup;
